import React, { useMemo } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import { toDateInputValue } from "../shared/dateTimeInput";
import { formatExecutionTimestampWithZone } from "../../Utils/timezone";
import { useDisplayTimeZone } from "../../Utils/displayTimeZone";

type TradeHeaderProps = {
  currencyPair: string;
  executionTimestampUtc: string; // e.g. "2026-01-19T18:05:00Z" or "01-19-2026 18:05 UTC"
  executionSpotRate?: number | null;
  executionForwardPoints?: number | null;
  expiryDateUtc?: string; // e.g. "2026-02-19"
};

function pipSizeForPair(pair: string) {
  // Common FX convention: JPY pairs pip = 0.01, otherwise 0.0001
  return pair.toUpperCase().includes("JPY") ? 0.01 : 0.0001;
}

function fmtNumber(x: number, dp = 5) {
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(dp);
}

function fmtMaybe(x?: number | null, dp = 5) {
  if (x == null || !Number.isFinite(x)) return "—";
  return x.toFixed(dp);
}

const useHeaderStyles = makeStyles((theme: any) => ({
  header: {
    position: "sticky",
    top: 0,
    zIndex: 0,
    backgroundColor: theme.globallink.colors.DarkGrey[500],
    padding: "0 0 8px",
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 6px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#E0E0E0",
    fontSize: "12px",
    lineHeight: "16px",
    whiteSpace: "nowrap",
  },
  detailsChip: {
    "@media (max-width: 1200px)": {
      display: "none",
    },
  },
  key: { opacity: 0.7 },
  value: { fontWeight: 700 },

  right: {
    marginLeft: "auto",
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
}));

export function BestXTradeHeader({
  currencyPair,
  executionTimestampUtc,
  executionSpotRate,
  expiryDateUtc,
  executionForwardPoints
}: TradeHeaderProps) {
  const theme = useTheme();
  const classes: any = useHeaderStyles({ theme });
  const { timeZone } = useDisplayTimeZone();

  const pipSize = useMemo(() => pipSizeForPair(currencyPair), [currencyPair]);
  const formattedExecutionTime = useMemo(
    () => formatExecutionTimestampWithZone(executionTimestampUtc, timeZone),
    [executionTimestampUtc, timeZone]
  );
  const executionForwardRate = useMemo(() => {
    if (executionSpotRate == null || executionForwardPoints == null) return null;
    if (!Number.isFinite(executionSpotRate) || !Number.isFinite(executionForwardPoints)) {
      return null;
    }
    return executionSpotRate + executionForwardPoints;
  }, [executionSpotRate, executionForwardPoints]);


  return (
    <div className={classes.header}>
      <div className={classes.row}>
        {/* Basic fields */}
        <span className={classes.chip}>
          <span className={classes.value}>{currencyPair}</span>
        </span>

        <span className={classes.chip}>
          <span className={classes.key}>Completion time</span>
          <span className={classes.value}>{formattedExecutionTime}</span>
        </span>

        <span className={`${classes.chip} ${classes.detailsChip}`}>
          <span className={classes.key}>Execution Spot Rate</span>
          <span className={classes.value}>
            {executionSpotRate == null ? "—" : fmtNumber(executionSpotRate, 4)}
          </span>
        </span>
        <span className={`${classes.chip} ${classes.detailsChip}`}>
          <span className={classes.key}>Execution Forward Points</span>
          <span className={classes.value}>
            {executionForwardPoints == null ? "—" : fmtNumber(executionForwardPoints, 4)}
          </span>
        </span>
        <span className={`${classes.chip} ${classes.detailsChip}`}>
          <span className={classes.key}>Execution Forward Rate</span>
          <span className={classes.value}>
            {executionForwardRate == null
              ? "—"
              : fmtNumber(executionForwardRate, 4)}
          </span>
        </span>
        <span className={`${classes.chip} ${classes.detailsChip}`}>
          <span className={classes.key}>Value Date</span>
          <span className={classes.value}>
            {toDateInputValue(expiryDateUtc) || "—"}
          </span>
        </span>

        {/* KPIs on the right */}
        
      </div>
    </div>
  );
}
